import type { DeliveryMethodType, Product, TailoredApiData } from '@libs/common-types';

export type QuantityOption = Readonly<{
    label: string;
    value: string;
}>;

type QuantityBounds = Readonly<{
    minQty: number;
    maxQty: number;
    stock: number;
    configuredMaxQty: number;
}>;

type QuantityBoundsInput = Readonly<{
    minimumQuantity?: number;
    maximumQuantity?: number;
    availableStock?: number;
}>;

const MIN_DISPLAY_QTY = 1;
export const MAX_DISPLAY_QTY = 500;

const getQuantityBounds = ({
    minimumQuantity,
    maximumQuantity,
    availableStock,
}: QuantityBoundsInput): QuantityBounds => {
    const configuredMaxQty = maximumQuantity ?? MAX_DISPLAY_QTY;

    return {
        minQty: minimumQuantity ?? MIN_DISPLAY_QTY,
        maxQty: Math.min(configuredMaxQty, MAX_DISPLAY_QTY),
        stock: availableStock ?? MAX_DISPLAY_QTY,
        configuredMaxQty,
    };
};

const getQuantityOptions = ({
    minQty,
    maxQty,
    stock,
}: Pick<QuantityBounds, 'minQty' | 'maxQty' | 'stock'>): QuantityOption[] => {
    const upperBound = Math.min(stock, maxQty);

    if (upperBound < minQty) return [];

    return Array.from({ length: upperBound - minQty + 1 }, (_, offset) => {
        const value = minQty + offset;

        return offset % minQty === 0 ? { label: String(value), value: String(value) } : null;
    }).filter((option): option is QuantityOption => option !== null);
};

const getQuantityFallback = ({
    quantity,
    minQty,
    items,
}: Readonly<{
    quantity: number;
    minQty: number;
    items: readonly QuantityOption[];
}>) => (items.some(item => item.value === String(quantity)) ? quantity : minQty);

type QuantityModelInput = Readonly<{
    product?: Product;
    tailored?: TailoredApiData | null;
    sku?: string;
    method: DeliveryMethodType;
    quantity: number;
    isMethodAvailable: boolean;
}>;

export type QuantityModel = Readonly<{
    items: QuantityOption[];
    minQty: number;
    stock: number;
    configuredMaxQty: number;
    fallback: number;
    isConfigurationValid: boolean;
    shouldRender: boolean;
}>;

export const getQuantityModel = ({
    product,
    tailored,
    sku,
    method,
    quantity,
    isMethodAvailable,
}: QuantityModelInput): QuantityModel => {
    const purchaseQuantities = product?.deliveryMethods[method].purchaseQuantities;
    const tailoredVariant = sku ? tailored?.variants[sku] : undefined;
    const bounds = getQuantityBounds({
        minimumQuantity: purchaseQuantities?.min,
        maximumQuantity: purchaseQuantities?.max,
        availableStock: tailoredVariant?.[method]?.availableStock ?? undefined,
    });
    const items = getQuantityOptions(bounds);
    const isConfigurationValid = {
        BOPIS: Boolean(tailoredVariant?.BOPIS?.storeId),
        SDD: Boolean(tailoredVariant?.SDD?.doorDashValidationSuccess),
        STH: !product?.pharmacy?.oos,
    };

    return {
        items,
        minQty: bounds.minQty,
        stock: bounds.stock,
        configuredMaxQty: bounds.configuredMaxQty,
        fallback: getQuantityFallback({ quantity, minQty: bounds.minQty, items }),
        isConfigurationValid: isConfigurationValid[method],
        shouldRender: Boolean(isMethodAvailable && isConfigurationValid[method] && items.length),
    };
};
